const WMO_CODES = {
  0:  { condition: 'Clear',          icon: '☀️' },
  1:  { condition: 'Mostly Clear',   icon: '🌤️' },
  2:  { condition: 'Partly Cloudy',  icon: '⛅' },
  3:  { condition: 'Overcast',       icon: '☁️' },
  45: { condition: 'Foggy',          icon: '🌫️' },
  48: { condition: 'Icy Fog',        icon: '🌫️' },
  51: { condition: 'Light Drizzle',  icon: '🌦️' },
  53: { condition: 'Drizzle',        icon: '🌦️' },
  55: { condition: 'Heavy Drizzle',  icon: '🌦️' },
  61: { condition: 'Light Rain',     icon: '🌧️' },
  63: { condition: 'Rain',           icon: '🌧️' },
  65: { condition: 'Heavy Rain',     icon: '🌧️' },
  71: { condition: 'Light Snow',     icon: '❄️' },
  73: { condition: 'Snow',           icon: '❄️' },
  75: { condition: 'Heavy Snow',     icon: '❄️' },
  77: { condition: 'Sleet',          icon: '🌨️' },
  80: { condition: 'Showers',        icon: '🌧️' },
  81: { condition: 'Heavy Showers',  icon: '🌧️' },
  82: { condition: 'Violent Shower', icon: '🌧️' },
  85: { condition: 'Snow Showers',   icon: '🌨️' },
  86: { condition: 'Heavy Snow Showers', icon: '🌨️' },
  95: { condition: 'Thunderstorm',   icon: '⛈️' },
  96: { condition: 'Stormy',         icon: '⛈️' },
  99: { condition: 'Severe Storm',   icon: '⛈️' },
}

function codeToWeather(code) {
  return WMO_CODES[code] ?? { condition: 'Unknown', icon: '🌡️' }
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

// Simple in-memory cache — 10 min TTL so 1-min polls stay fresh without hammering the API
let _cache = null
let _cacheTime = 0
let _cacheKey = ''
const CACHE_TTL = 10 * 60 * 1000

export async function fetchWeather(lat, lon, unit = 'fahrenheit') {
  const key = `${lat},${lon},${unit}`
  if (_cache && _cacheKey === key && Date.now() - _cacheTime < CACHE_TTL) {
    return _cache
  }

  const params = new URLSearchParams({
    latitude: lat,
    longitude: lon,
    current: 'temperature_2m,weathercode,windspeed_10m,apparent_temperature',
    daily: 'weathercode,temperature_2m_max,temperature_2m_min,precipitation_probability_max',
    temperature_unit: unit,
    timezone: 'auto',
    forecast_days: '4',
  })

  const res = await fetch(`https://api.open-meteo.com/v1/forecast?${params}`)
  if (!res.ok) throw new Error('Weather API error')
  const json = await res.json()

  const cur = json.current
  const daily = json.daily

  const result = {
    current: {
      temperature: Math.round(cur.temperature_2m),
      feelsLike:   Math.round(cur.apparent_temperature),
      windSpeed:   Math.round(cur.windspeed_10m),
      ...codeToWeather(cur.weathercode),
    },
    forecast: (daily.time ?? []).slice(0, 4).map((dateStr, i) => {
      const d = new Date(dateStr + 'T12:00:00')
      return {
        date:         dateStr,
        dayName:      i === 0 ? 'Today' : DAY_NAMES[d.getDay()],
        high:         Math.round(daily.temperature_2m_max[i]),
        low:          Math.round(daily.temperature_2m_min[i]),
        precipChance: daily.precipitation_probability_max[i] ?? 0,
        ...codeToWeather(daily.weathercode[i]),
      }
    }),
  }

  _cache = result
  _cacheTime = Date.now()
  _cacheKey = key
  return result
}

// Reverse geocode lat/lon → "City, State" using Nominatim (no API key required)
export async function reverseGeocode(lat, lon) {
  const res = await fetch(
    `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`,
    { headers: { 'User-Agent': 'DoorChart/1.0' } },
  )
  if (!res.ok) throw new Error('Reverse geocoding failed')
  const json = await res.json()
  const a = json.address ?? {}
  const city  = a.city || a.town || a.village || a.county || ''
  const state = a.state || ''
  return [city, state].filter(Boolean).join(', ')
}

// Forward geocode city name → { lat, lon, label } using Open-Meteo geocoding API
export async function geocodeCity(name) {
  const res = await fetch(
    `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(name)}&count=1&language=en&format=json`,
  )
  if (!res.ok) throw new Error('Geocoding failed')
  const json = await res.json()
  const r = json.results?.[0]
  if (!r) throw new Error('City not found')
  const label = [r.name, r.admin1].filter(Boolean).join(', ')
  return { lat: r.latitude, lon: r.longitude, label }
}
